import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { CheckboxComponent } from '@app/components/checkbox/checkbox.component';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { FileDropzoneComponent } from '../../../upload-images/field-photo-upload-modal/file-dropzone/file-dropzone.component';
import { GeoImageUploadCardComponent } from './geo-image-upload-card/geo-image-upload-card.component';
import { GeoImageCoordinateModalComponent } from './geo-image-coordinate-modal/geo-image-coordinate-modal.component';
import { GeoImageCard, isAcceptableGeoImageFile } from './geo-image-upload.models';
import { GeoImageFileValidator } from '../services/geo-image-file-validator.util';
import { UploadGeoImageBatchItemPayload, UploadGeoImageBatchResponse, UploadedGeoImageService } from '../services/uploaded-geo-image.service';

/**
 * "Upload preview" — the checkbox-and-counter preview step the reference mockup shows. The
 * Electronic Field Map upload modal has no equivalent gating step (it uploads sequentially on submit); this is
 * genuinely new UI (FR-007/FR-008), reusing only the dropzone and per-card layout shape from it.
 */
@Component({
  selector: 'app-geo-image-upload-modal',
  standalone: true,
  templateUrl: './geo-image-upload-modal.component.html',
  styleUrls: ['./geo-image-upload-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogComponent,
    PrimaryButton,
    SecondaryButton,
    CheckboxComponent,
    ToastModule,
    FileDropzoneComponent,
    GeoImageUploadCardComponent,
    GeoImageCoordinateModalComponent
  ]
})
export class GeoImageUploadModalComponent implements OnChanges, OnDestroy {
  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement> | null = null;

  @Input() visible = false;
  /** When opened from a map PKM, prefills the next staged photo card. */
  @Input() initialCoordinates: {lat: number; lng: number} | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() uploaded = new EventEmitter<UploadGeoImageBatchResponse>();
  @Output() cancel = new EventEmitter<void>();

  readonly MAX_TOTAL_SIZE = 500 * 1024 * 1024;

  cards: GeoImageCard[] = [];
  errors: string[] = [];
  isProcessing = false;

  coordinateModalVisible = false;
  private coordinateModalCardIndex: number | null = null;
  private uploadSubscription: Subscription | null = null;
  private pendingMapCoordinates: {latitude: number; longitude: number} | null = null;

  constructor(
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly validator: GeoImageFileValidator,
    private readonly uploadedGeoImageService: UploadedGeoImageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetForm();
      this.applyInitialCoordinates();
    }
    if (changes['initialCoordinates']) {
      this.applyInitialCoordinates();
    }
  }

  ngOnDestroy(): void {
    this.uploadSubscription?.unsubscribe();
    this.cards.forEach(card => URL.revokeObjectURL(card.previewUrl));
  }

  onFilesSelected(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    const acceptable = files.filter(isAcceptableGeoImageFile);
    if (acceptable.length === 0) {
      this.errors = ['UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_TYPE'];
      this.cdr.detectChanges();
      return;
    }

    // Matches the actual upload payload (handleStartProcessing sends only selectedCards), so this
    // gate no longer blocks adding a small file just because unrelated unselected/rejected cards are
    // already staged.
    const currentTotalSize = this.selectedCards.reduce((sum, c) => sum + c.file.size, 0);
    const newFilesSize = acceptable.reduce((sum, f) => sum + f.size, 0);
    if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
      this.errors = ['UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_SIZE_EXCEEDED'];
      this.cdr.detectChanges();
      return;
    }

    this.errors = [];

    if (acceptable.length < files.length) {
      const rejectedCount = files.length - acceptable.length;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_TYPE'),
        detail: `${rejectedCount} ${this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_TYPE')}`,
        life: 5000
      });
    }

    const duplicates: File[] = [];
    acceptable.forEach(file => {
      if (this.isDuplicate(file)) {
        duplicates.push(file);
      } else {
        this.addCard(file);
      }
    });

    if (duplicates.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_ALREADY_ADDED'),
        detail:
          duplicates.length === 1
            ? duplicates[0].name
            : `${duplicates.length} ${this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_ERROR_FILE_ALREADY_ADDED')}`,
        life: 5000
      });
    }

    this.cdr.detectChanges();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this.onFilesSelected(files);
    input.value = '';
  }

  triggerFileSelect(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.fileInputRef?.nativeElement.click();
  }

  private isDuplicate(file: File): boolean {
    return this.cards.some(c => c.file.name === file.name && c.file.size === file.size && c.file.lastModified === file.lastModified);
  }

  private applyInitialCoordinates(): void {
    if (!this.initialCoordinates) {
      this.pendingMapCoordinates = null;
      return;
    }
    this.pendingMapCoordinates = {
      latitude: this.initialCoordinates.lat,
      longitude: this.initialCoordinates.lng,
    };
  }

  private addCard(file: File): void {
    const pendingCoords = this.pendingMapCoordinates;
    const card: GeoImageCard = {
      file,
      previewUrl: URL.createObjectURL(file),
      title: file.name.replace(/\.[^/.]+$/, ''),
      coordinates: pendingCoords,
      footprintWkt: null,
      captureDate: null,
      tagIds: [],
      imageKind: null,
      rejectionReason: null,
      isChecking: true,
      selected: !!pendingCoords,
      errors: []
    };
    if (pendingCoords) {
      this.pendingMapCoordinates = null;
    }
    this.cards.push(card);
    // Looked up by `file` (stable for this card's lifetime, unlike an index the user can shift by
    // removing an earlier card) rather than passed by reference: the validator's patches arrive
    // asynchronously, after `this.cards[i]` has already been replaced with a new object by an earlier
    // patch, so patching a captured `card` reference in place would silently update a stale, detached
    // object that no longer matches anything in `this.cards` — and the child card component (OnPush)
    // is bound to `this.cards[i]` by object identity, so only replacing that array element, never
    // mutating a card object's properties, makes the update visible.
    this.validator.checkCard(card, patch => this.patchCard(file, patch));
  }

  /** Replaces the card matching `file` with a copy carrying `patch`, so the OnPush card component sees a new `@Input` reference. */
  private patchCard(file: File, patch: Partial<GeoImageCard>): void {
    const index = this.cards.findIndex(c => c.file === file);
    if (index < 0) {
      return;
    }
    this.cards[index] = { ...this.cards[index], ...patch };
    this.cdr.markForCheck();
  }

  onCardChange(index: number, updated: GeoImageCard): void {
    this.cards[index] = updated;
    this.cdr.markForCheck();
  }

  trackByCard(_index: number, card: GeoImageCard): File {
    return card.file;
  }

  applyFirstCardToAll(): void {
    if (this.cards.length === 0) return;
    const { title, tagIds } = this.cards[0];
    // Replace, don't mutate: the card component is OnPush, so it only re-renders when its `[card]`
    // input receives a new object reference (see the identical pattern in onCardChange above).
    for (let i = 1; i < this.cards.length; i++) {
      this.cards[i] = { ...this.cards[i], title, tagIds: [...tagIds] };
    }
    this.cdr.detectChanges();
  }

  removeCard(index: number): void {
    if (index < 0 || index >= this.cards.length) return;
    URL.revokeObjectURL(this.cards[index].previewUrl);
    this.cards.splice(index, 1);
    this.cdr.detectChanges();
  }

  openCoordinateModal(index: number): void {
    this.coordinateModalCardIndex = index;
    this.coordinateModalVisible = true;
    this.cdr.markForCheck();
  }

  onCoordinatesConfirmed(coordinates: { latitude: number; longitude: number }): void {
    if (this.coordinateModalCardIndex == null) return;
    const index = this.coordinateModalCardIndex;
    const card = this.cards[index];
    if (card) {
      this.cards[index] = { ...card, coordinates, rejectionReason: null, selected: true };
    }
    this.coordinateModalCardIndex = null;
    this.cdr.detectChanges();
  }

  toggleSelectAll(selectAll: boolean): void {
    this.cards = this.cards.map(card => (this.canSelect(card) ? { ...card, selected: selectAll } : card));
    this.cdr.detectChanges();
  }

  private canSelect(card: GeoImageCard): boolean {
    if (card.isChecking || card.rejectionReason) return false;
    return card.imageKind === 'Raster' || (card.imageKind === 'Photo' && card.coordinates != null);
  }

  get allSelectableChecked(): boolean {
    const selectable = this.cards.filter(c => this.canSelect(c));
    return selectable.length > 0 && selectable.every(c => c.selected);
  }

  get selectedCards(): GeoImageCard[] {
    return this.cards.filter(c => c.selected);
  }

  get selectedCount(): number {
    return this.selectedCards.length;
  }

  get selectedTotalSizeMb(): string {
    const bytes = this.selectedCards.reduce((sum, c) => sum + c.file.size, 0);
    return (bytes / (1024 * 1024)).toFixed(1);
  }

  get hasAnyStillChecking(): boolean {
    return this.cards.some(c => c.isChecking);
  }

  handleCancel(): void {
    // Actually abort a pending request rather than merely closing the dialog on top of it — otherwise
    // the batch still completes and silently emits `uploaded` to a parent that believes it was cancelled.
    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = null;
    this.isProcessing = false;
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  handleStartProcessing(): void {
    if (this.isProcessing || this.selectedCount === 0) {
      return;
    }

    const selected = this.selectedCards;
    const files = selected.map(c => c.file);
    const items: UploadGeoImageBatchItemPayload[] = selected.map(c => ({
      title: c.title?.trim() || c.file.name,
      tagIds: c.tagIds,
      pointJson: c.imageKind === 'Photo' && c.coordinates ? `POINT(${c.coordinates.longitude} ${c.coordinates.latitude})` : null
    }));

    this.isProcessing = true;
    this.cdr.detectChanges();

    this.uploadSubscription = this.uploadedGeoImageService.uploadBatch(files, items).subscribe({
      next: response => {
        this.isProcessing = false;
        this.uploadSubscription = null;
        if (response.skipped.length > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_ERROR_UPLOAD_FAILED'),
            detail: this.translate.instant('UPLOADED_GEO_IMAGES.MODAL_UPLOAD_PARTIAL_SKIPPED', { count: response.skipped.length }),
            life: 8000
          });
        }
        this.uploaded.emit(response);
        this.visibleChange.emit(false);
      },
      error: () => {
        this.isProcessing = false;
        this.uploadSubscription = null;
        this.errors = ['UPLOADED_GEO_IMAGES.MODAL_ERROR_UPLOAD_FAILED'];
        this.cdr.detectChanges();
      }
    });
  }

  private resetForm(): void {
    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = null;
    this.errors = [];
    this.isProcessing = false;
    this.coordinateModalVisible = false;
    this.coordinateModalCardIndex = null;
    this.cards.forEach(card => URL.revokeObjectURL(card.previewUrl));
    this.cards = [];
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
}
